import { ReportColumn, ReportFilter, ReportProps } from "@app/api/reports.api";
import { BaseButton } from "@app/components/common/BaseButton/BaseButton";
import { BaseCol } from "@app/components/common/BaseCol/BaseCol";
import { BaseRadio } from "@app/components/common/BaseRadio/BaseRadio";
import { BaseRow } from "@app/components/common/BaseRow/BaseRow";
import { BaseSpace } from "@app/components/common/BaseSpace/BaseSpace";
import { BaseSwitch } from "@app/components/common/BaseSwitch/BaseSwitch";
import { BaseButtonsForm } from "@app/components/common/forms/BaseButtonsForm/BaseButtonsForm";
import { BaseForm } from "@app/components/common/forms/BaseForm/BaseForm";
import { BaseInput } from "@app/components/common/inputs/BaseInput/BaseInput";
import { DayjsDatePicker } from "@app/components/common/pickers/DayjsDatePicker";
import { BaseSelect } from "@app/components/common/selects/BaseSelect/BaseSelect";
import { GlobalBackButton } from "@app/components/global/GlobalBackButton";
import { Select, DatePicker, InputNumber, TablePaginationConfig, TableProps } from "antd";
import moment from 'moment';
import { forwardRef, useEffect, useState } from "react";
import * as S from "../../tables/Tables/Tables.styles";
import * as R from "./ReportViewer.styles";
import * as C from "./ReportSearchResultComponent.styles";
import { useTranslation } from "react-i18next";
import { useMounted } from "@app/hooks/useMounted";
import { IList } from "@app/entities/IListResponse";
import * as XLSX from 'xlsx'; // Import the xlsx library
import { useNavigate } from "react-router-dom";
import { exportToPdf } from "@app/utils/pdfUtils";

interface TableParams {
  pagination?: TablePaginationConfig;
  // sortField?: SorterResult<any>['field'];
  // sortOrder?: SorterResult<any>['order'];
}
interface ModalProps {
  closeSubReport?: () => void;
  reportProps: ReportProps;
  reportFiltersProps: ReportFilter[];
  reportColumnsProps: ReportColumn[];
  systemName: string;
  hideTitle: boolean;
  onSearch: (filterBuilder: any, pageSize: number, current: number) => Promise<IList<any>>;
  onDownload: (filterBuilder: any) => Promise<any>;
  openSubReport?: (route: string) => void;
}

const ReportSearchResultComponent = forwardRef((props: ModalProps, ref) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = BaseForm.useForm();
  const { isMounted } = useMounted();
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });
  const [reportGenerated, setReportGenerated] = useState(false);
  const [isSearching, setSearching] = useState(false);
  const [isExporting, setExporting] = useState(false);
  const [report, setReport] = useState<ReportProps>();
  const [columns, setColumns] = useState<any[]>([]);
  const [reportFilters, setReportFilters] = useState<ReportFilter[]>([]);
  const [result, setResult] = useState<any[]>([]);

  useEffect(() => {
    generateReport();
  }, [isMounted]);

  useEffect(() => {
    if (reportGenerated) {
        searchReport(tableParams?.pagination?.pageSize ?? 10, tableParams?.pagination?.current ?? 1);
    }
  }, [
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    reportGenerated,
  ]);
  
  const generateRoute = (template: string, reportColumn: ReportColumn) => {
    const params: Record<string, string> = Object.entries(reportColumn).reduce((acc, [key, value]) => {
      acc[key] = String(value); // Ensure all values are strings
      return acc;
    }, {} as Record<string, string>)

    return template.replace(/:(\w+)/g, (_, key) => {
      if (params[key] === undefined) {
        throw new Error(`Missing value for route parameter: ${key}`);
      }
      return params[key];
    });
  };

  const convertCardSets = (data: string) => {
    if (data && data.length > 0) {
      const jsonFormat = JSON.parse(data);
      const formattedData = jsonFormat.map((item: any) => {
        return <div>{`${item.cardTypeName} ${item.cardColorName} ${item.hasInnerBox ? ` (#ID: ${item.innerBoxStockId})` : ''}`}</div>
      });
      return <C.CardSetContainer>{formattedData}</C.CardSetContainer>;
    }

    return <></>;
  }

  const convertCardSetsForExcel = (data: string): string => {
    if (data && data.length > 0) {
      const jsonFormat = JSON.parse(data);
      const formattedData = jsonFormat.map((item: any) => {
        return `${item.cardTypeName} ${item.cardColorName} ${item.hasInnerBox ? ` (#ID: ${item.innerBoxStockId})` : ''}`
      });
      return formattedData.join('\n');
    }

    return "";
  }

  const convertScanPersonnelForExcel = (data: string): string => {
    if (data && data.length > 0) {
      const jsonFormat = JSON.parse(data);
      const formattedData = jsonFormat.map((item: any) => {
        return `${item.displayTitle}: ${item.scannedEmployeeId}`
      });
      return formattedData.join('\n');
    }

    return "";
  } 

  const convertScanPersonnel = (data: string) => {
    if (data && data.length > 0) {
      const jsonFormat = JSON.parse(data);
      const formattedData = jsonFormat.map((item: any) => {
        return <div><strong>{item.displayTitle}:</strong> {item.scannedEmployeeId}</div>
      });
      return <C.ScanPersonnelContainer>{formattedData}</C.ScanPersonnelContainer>;
    }
    
    return <></>;
  } 

  const generateReport = () => {
    if (isMounted) {
        setReport(props.reportProps);
        setReportFilters(props.reportFiltersProps);

        const initialFormValues: Record<string, any> = props.reportFiltersProps.reduce(
            (acc: Record<string, any>, filter: ReportFilter) => {
                if (filter.defaultValue) {
                    acc[filter.fieldName] = filter.defaultValue; // Dynamically set the key-value
                }
              return acc;
            },
            {}
        );

        const formattedColumns = props.reportColumnsProps.map((column: ReportColumn, index: number) => {
            if (column.displayType == 'LINK') {
                return {
                    title: column.displayName,
                    key: `${column.fieldName}_${index}`,
                    dataIndex: column.fieldName,
                    displayType: column.displayType,
                    render: (text: string, record: ReportColumn) => {
                        return (
                            <BaseButton type="link" onClick={() => {
                              if (props.openSubReport) props.openSubReport(generateRoute(column.route, record));
                            }}>
                                {text}
                            </BaseButton>
                        );
                    }
                }
            } else if (column.displayType == 'EXTERNAL_LINK') {
              return {
                title: column.displayName,
                key: `${column.fieldName}_${index}`,
                dataIndex: column.fieldName,
                displayType: column.displayType,
                render: (text: string, record: ReportColumn) => {
                    return (
                        <BaseButton type="link" onClick={() => {
                          navigate(generateRoute(column.route, record));
                        }}>
                            {text}
                        </BaseButton>
                    );
                }
            }
          } else if (column.displayType == 'BUTTON') {
            return {
                title: column.displayName,
                key: `${column.fieldName}_${index}`,
                dataIndex: column.fieldName,
                displayType: column.displayType,
                render: (text: string, record: ReportColumn) => {
                    return (
                        <BaseButton type="default" onClick={() => {
                          if (props.openSubReport) props.openSubReport(column.route);
                        }}>
                            {column.displayName}
                        </BaseButton>
                    );
                }
            }
          } else if (column.displayType == 'EXTERNAL_BUTTON') {
            return {
                title: column.displayName,
                key: `${column.fieldName}_${index}`,
                dataIndex: column.fieldName,
                displayType: column.displayType,
                render: (text: string, record: ReportColumn) => {
                    return (
                        <BaseButton type="default" onClick={() => {
                          navigate(generateRoute(column.route, record));
                        }}>
                            {column.displayName}
                        </BaseButton>
                    );
                }
            }
          } else if (column.displayType == 'CARD_SETS')  {
              return {
                  title: column.displayName,
                  key: `${column.fieldName}_${index}`,
                  dataIndex: column.fieldName,
                  displayType: column.displayType,
                  render: (text:string) => {
                    return convertCardSets(text)
                  }
              }
            } else if (column.displayType == 'SCAN_PERSONNEL')  {
              return {
                  title: column.displayName,
                  key: `${column.fieldName}_${index}`,
                  dataIndex: column.fieldName,
                  displayType: column.displayType,
                  render: (text:any) => {
                    return convertScanPersonnel(text)
                  }
              }
            } else {
                return {
                    title: column.displayName,
                    key: `${column.fieldName}_${index}`,
                    dataIndex: column.fieldName,
                    displayType: column.displayType,
                }
            }
        });

        setColumns(formattedColumns);
        setReportGenerated(true);
    }
  };

  const searchReport = async (pageSize: number, current: number) => {
    setSearching(true);
    // gather search filters
    const searchValues = form.getFieldsValue(true);
    const filtersBuilder:  Record<string, any> = {};
    reportFilters.forEach((filter) => {
        switch (filter.inputType) {
            case "TEXT":
            case "NUMBER":
            case 'DROPDOWN':
            case 'DYNAMIC_DROPDOWN':
            case 'RADIO_BUTTON':
            case "HIDDEN":
                filtersBuilder[filter.fieldName] = searchValues[filter.fieldName] ? searchValues[filter.fieldName] : '';
                break;
            case 'BOOLEAN':
                filtersBuilder[filter.fieldName] = searchValues[filter.fieldName] ? "Yes" : "No";
                break;
            case 'DATE':
                filtersBuilder[filter.fieldName] = searchValues[filter.fieldName] ? moment(searchValues[filter.fieldName]).format('YYYY-MM-DD') : '';
                break;
            case 'DATE_TIME':
                filtersBuilder[filter.fieldName] = searchValues[filter.fieldName] ? moment(searchValues[filter.fieldName]).format('YYYY-MM-DD HH:mm:ss') : '';
                break;
            case 'DATE_RANGE':
                if (searchValues[filter.fieldName]) {
                    const dateRange = searchValues[filter.fieldName] ?? searchValues[filter.fieldName].split(',');
                    if (dateRange.length === 2) {
                        const fromDate = moment(new Date(dateRange[0])).format('YYYY-MM-DD');
                        const toDate = moment(new Date(dateRange[1])).format('YYYY-MM-DD');
                        if (fromDate && toDate) {
                            filtersBuilder[filter.fieldName] = `${fromDate},${toDate}`;   
                        }
                    }
                }
                break;
            case 'DATE_TIME_RANGE':
                if (searchValues[filter.fieldName]) { 
                    const dateTimeRange = searchValues[filter.fieldName] ?? searchValues[filter.fieldName].split(',');
                    if (dateTimeRange.length === 2) {
                        const fromDate = moment(new Date(dateTimeRange[0])).format('YYYY-MM-DD HH:mm:ss');
                        const toDate = moment(new Date(dateTimeRange[1])).format('YYYY-MM-DD HH:mm:ss');
                        if (fromDate && toDate) {
                            filtersBuilder[filter.fieldName] = `${fromDate},${toDate}`;   
                        }
                    }
                }
                break;
        }
    });

    // call search api and get results
    const result = await props.onSearch(filtersBuilder, pageSize, current);
    if (result && isMounted.current) {
        setResult(result.data);
        setTableParams({
            pagination: {
                current: result.currentPageNumber,
                pageSize: result.pageSize,
                total: result.totalRecords
            }
        });
    }
    setSearching(false);
  }

  const handleTableChange: TableProps<any>["onChange"] = (
    pagination,
    _filters,
    _sorter
  ) => {
    setTableParams({
      pagination,
    });

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setResult([]);
    }
  };

  const submitSearch = () => {
    searchReport(
      tableParams?.pagination?.pageSize ?? 10,
      tableParams?.pagination?.current ?? 1
    );
  };

  const downloadReport = async() => {
    setExporting(true);
    const filters = form.getFieldsValue(true);
    const filtersBuilder:  Record<string, any> = {};
    reportFilters.forEach((filter) => {
        switch (filter.inputType) {
            case "TEXT":
            case "NUMBER":
            case 'DROPDOWN':
            case 'DYNAMIC_DROPDOWN':
            case 'RADIO_BUTTON':
            case "HIDDEN":
                filtersBuilder[filter.fieldName] = filters[filter.fieldName] ? filters[filter.fieldName] : '';
                break;
            case 'BOOLEAN':
                filtersBuilder[filter.fieldName] = filters[filter.fieldName] ? "Yes" : "No";
                break;
            case 'DATE':
                filtersBuilder[filter.fieldName] = filters[filter.fieldName] ? moment(filters[filter.fieldName]).format('YYYY-MM-DD') : '';
                break;
            case 'DATE_TIME':
                filtersBuilder[filter.fieldName] = filters[filter.fieldName] ? moment(filters[filter.fieldName]).format('YYYY-MM-DD HH:mm:ss') : '';
                break;
            case 'DATE_RANGE':
                if (filters[filter.fieldName]) {
                    const dateRange = filters[filter.fieldName] ?? filters[filter.fieldName].split(',');
                    if (dateRange.length === 2) {
                        const fromDate = moment(new Date(dateRange[0])).format('YYYY-MM-DD');
                        const toDate = moment(new Date(dateRange[1])).format('YYYY-MM-DD');
                        if (fromDate && toDate) {
                            filtersBuilder[filter.fieldName] = `${fromDate},${toDate}`;   
                        }
                    }
                }
                break;
            case 'DATE_TIME_RANGE':
                if (filters[filter.fieldName]) { 
                    const dateTimeRange = filters[filter.fieldName] ?? filters[filter.fieldName].split(',');
                    if (dateTimeRange.length === 2) {
                        const fromDate = moment(new Date(dateTimeRange[0])).format('YYYY-MM-DD HH:mm:ss');
                        const toDate = moment(new Date(dateTimeRange[1])).format('YYYY-MM-DD HH:mm:ss');
                        if (fromDate && toDate) {
                            filtersBuilder[filter.fieldName] = `${fromDate},${toDate}`;   
                        }
                    }
                }
                break;
        }
    });

    // call search excel api and get results
    const result = await props.onDownload(filtersBuilder);
    if (result && isMounted.current) {
        const transformedData = transformData(result.list);
        
        const ws = XLSX.utils.json_to_sheet(transformedData); // Convert data to worksheet
        
        // Adjust column width
        const setColumnWidths = (data: any) => {
          const columnWidths = Object.keys(data[0]).map((key) => ({
            wch: Math.max(
              key.length, // Header length
              ...data.map((row: any) => (row[key] ? row[key].toString().length : 0)) // Content length
            ),
          }));
          ws["!cols"] = columnWidths;
        };

        setColumnWidths(transformedData);
        
        const wb = XLSX.utils.book_new(); // Create a new workbook
        XLSX.utils.book_append_sheet(wb, ws, "Reports"); // Add the worksheet to the workbook
        XLSX.writeFile(wb, `${props.systemName?.toString()}_report_${getCurrentDateTime()}.xlsx`); // Save the workbook as a file
    }
    setExporting(false);
  }

const downloadPdfReport = async () => {
  setExporting(true);
  const filters = form.getFieldsValue(true);
  const filtersBuilder: Record<string, any> = {};

  reportFilters.forEach((filter) => {
    switch (filter.inputType) {
      case "TEXT":
      case "NUMBER":
      case "DROPDOWN":
      case "DYNAMIC_DROPDOWN":
      case "RADIO_BUTTON":
      case "HIDDEN":
        filtersBuilder[filter.fieldName] = filters[filter.fieldName] || '';
        break;
      case "BOOLEAN":
        filtersBuilder[filter.fieldName] = filters[filter.fieldName] ? "Yes" : "No";
        break;
      case "DATE":
        filtersBuilder[filter.fieldName] = filters[filter.fieldName]
          ? moment(filters[filter.fieldName]).format("YYYY-MM-DD")
          : '';
        break;
      case "DATE_TIME":
        filtersBuilder[filter.fieldName] = filters[filter.fieldName]
          ? moment(filters[filter.fieldName]).format("YYYY-MM-DD HH:mm:ss")
          : '';
        break;
      case "DATE_RANGE":
        const dr = filters[filter.fieldName];
        if (dr?.length === 2) {
          const from = moment(dr[0]).format("YYYY-MM-DD");
          const to = moment(dr[1]).format("YYYY-MM-DD");
          filtersBuilder[filter.fieldName] = `${from},${to}`;
        }
        break;
      case "DATE_TIME_RANGE":
        const dtr = filters[filter.fieldName];
        if (dtr?.length === 2) {
          const from = moment(dtr[0]).format("YYYY-MM-DD HH:mm:ss");
          const to = moment(dtr[1]).format("YYYY-MM-DD HH:mm:ss");
          filtersBuilder[filter.fieldName] = `${from},${to}`;
        }
        break;
    }
  });

  const result = await props.onDownload(filtersBuilder);
  if (result && isMounted.current) {
    exportToPdf(columns, result.list, props.systemName);
  }

  setExporting(false);
};


  const transformData = (data: any) => {
    const transformedData: Record<string, any>[] = [];    

    data.map((item: any) => {
      const transformedRow: Record<string, any> = {};
      columns.filter((column) => column.displayType !== 'BUTTON' && column.displayType !== 'EXTERNAL_BUTTON').forEach((column: any) => {

        if (column.displayType == 'CARD_SETS') {
          transformedRow[column.title] = convertCardSetsForExcel(item[column.dataIndex]);
        } else if (column.displayType == 'SCAN_PERSONNEL') {
          transformedRow[column.title] = convertScanPersonnelForExcel(item[column.dataIndex]);
        } else {
          transformedRow[column.title] = item[column.dataIndex];
        }
      });

      transformedData.push(transformedRow);
    });


    return transformedData;
  }

  const getCurrentDateTime = () => {
    const now = new Date();

    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0"); // Months are zero-based
    const day = String(now.getDate()).padStart(2, "0");

    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");

    return `${year}${month}${day}-${hours}${minutes}${seconds}`;
  };

  const clearSearch = () => {
    const fields = form.getFieldsValue();
    const clearedFields = Object.keys(fields).reduce(
      (acc: any, key: string) => {
        acc[key] = ""; // Or '' for empty strings, depending on your use case
        return acc;
      },
      {}
    );

    form.setFieldsValue(clearedFields);

    setTableParams({
      ...tableParams,
      pagination: { ...tableParams.pagination, current: 1, pageSize: 10 },
    });
    searchReport(10, 1);
  };

  const handleBackButton = () => {
    return props.closeSubReport ? props.closeSubReport() : navigate(-1);
  };

  return (
    <>
      <S.TablesWrapper>
        <S.Card
          id="basic-table"
          title={props.hideTitle ? '' : `${t("reports.viewReport")} - ${
            report?.name || props.systemName
          }`}
          padding="1.25rem 1.25rem"
        >
          <BaseForm form={form} layout="vertical" name="reportSearchForm">
            <BaseRow gutter={5} style={{ width: "100%" }}>
              {report &&
                report.reportFilters.length > 0 &&
                report.reportFilters
                  .filter((filter) => filter.inputType !== "HIDDEN")
                  .map((filter, index) => {
                    return (
                      <>
                        <BaseCol xs={24} md={12} xl={12}>
                          {filter.inputType == "TEXT" && (
                            <BaseForm.Item
                              name={filter.fieldName}
                              label={filter.name}
                            >
                              <BaseInput
                                placeholder={
                                  filter.placeholder ? filter.placeholder : ""
                                }
                                value={
                                  filter.defaultValue ? filter.defaultValue : ""
                                }
                              />
                            </BaseForm.Item>
                          )}
                          {(filter.inputType == "DROPDOWN" ||
                            filter.inputType == "DYNAMIC_DROPDOWN") && (
                            <BaseButtonsForm.Item
                              name={filter.fieldName}
                              label={filter.name}
                            >
                              <BaseSelect
                                placeholder={
                                  filter.placeholder ? filter.placeholder : ""
                                }
                              >
                                {filter.listItem
                                  .split(",")
                                  .map((item, index) => {
                                    let value = item;
                                    let label = item;
                                    if (item.indexOf("=") >= 0) {
                                      const parts = item.split("=");
                                      value = parts[1];
                                      label = parts[0];
                                    }

                                    return (
                                      <Select.Option id={index} value={value}>
                                        {label}
                                      </Select.Option>
                                    );
                                  })}
                              </BaseSelect>
                            </BaseButtonsForm.Item>
                          )}
                          {filter.inputType == "RADIO_BUTTON" && (
                            <BaseForm.Item
                              name={filter.fieldName}
                              label={filter.name}
                            >
                              <BaseRadio.Group>
                                {filter.listItem.split(",").map((item) => {
                                  let value = item;
                                  let label = item;
                                  if (item.indexOf("=") >= 0) {
                                    const parts = item.split("=");
                                    value = parts[1];
                                    label = parts[0];
                                  }

                                  return (
                                    <BaseRadio.Button value={value}>
                                      {label}
                                    </BaseRadio.Button>
                                  );
                                })}
                              </BaseRadio.Group>
                            </BaseForm.Item>
                          )}
                          {filter.inputType == "BOOLEAN" && (
                            <BaseButtonsForm.Item
                              name={filter.fieldName}
                              label={filter.name}
                              valuePropName="checked"
                            >
                              <BaseSwitch />
                            </BaseButtonsForm.Item>
                          )}
                          {filter.inputType == "DATE" && (
                            <BaseButtonsForm.Item
                              name={filter.fieldName}
                              label={filter.name}
                            >
                              <DatePicker format="YYYY-MM-DD" />
                            </BaseButtonsForm.Item>
                          )}
                          {filter.inputType == "NUMBER" && (
                            <BaseButtonsForm.Item
                              name={filter.fieldName}
                              label={filter.name}
                            >
                              <InputNumber />
                            </BaseButtonsForm.Item>
                          )}
                          {filter.inputType == "DATE_TIME" && (
                            <BaseButtonsForm.Item
                              name={filter.fieldName}
                              label={filter.name}
                            >
                              <DayjsDatePicker format="YYYY-MM-DD HH:mm:ss" />
                            </BaseButtonsForm.Item>
                          )}
                          {filter.inputType == "DATE_RANGE" && (
                            <BaseButtonsForm.Item
                              name={filter.fieldName}
                              label={filter.name}
                            >
                              <DayjsDatePicker.RangePicker format="YYYY-MM-DD" />
                            </BaseButtonsForm.Item>
                          )}
                          {filter.inputType == "DATE_TIME_RANGE" && (
                            <BaseButtonsForm.Item
                              name={filter.fieldName}
                              label={filter.name}
                            >
                              <DayjsDatePicker.RangePicker
                                showTime
                                format="YYYY-MM-DD HH:mm:ss"
                              />
                            </BaseButtonsForm.Item>
                          )}
                        </BaseCol>
                      </>
                    );
                  })}
              <BaseCol xs={24} md={24} xl={24}>
                <BaseSpace style={{ float: "right" }}>
                  <BaseForm.Item>
                    <GlobalBackButton onClick={handleBackButton} />
                  </BaseForm.Item>
                  <BaseForm.Item>
                    <BaseButton
                      type="default"
                      loading={isExporting}
                      onClick={() => downloadReport()}
                    >
                      {t("Export to Excel")}
                    </BaseButton>
                  </BaseForm.Item>
                    <BaseForm.Item>
            <BaseButton
              type="default"
              loading={isExporting}
              onClick={downloadPdfReport}
            >
              Export to PDF
            </BaseButton>
          </BaseForm.Item>
                  <BaseForm.Item>
            
              </BaseForm.Item>
                  {
                    reportFilters && reportFilters.filter(filter => filter.inputType !== "HIDDEN").length > 0 && (
                        <>
                            <BaseForm.Item>
                                <BaseButton type="ghost" onClick={() => clearSearch()}>
                                {"Clear"}
                                </BaseButton>
                            </BaseForm.Item>
                            <BaseForm.Item>
                                <BaseButton
                                    type="primary"
                                    loading={isSearching}
                                    onClick={() => submitSearch()}
                                >
                                    {"Search"}
                                </BaseButton>
                            </BaseForm.Item>
                        </>
                    )
                  }
                </BaseSpace>
              </BaseCol>
            </BaseRow>
          </BaseForm>
          <R.StyledTable
            rowKey="id"
            columns={columns}
            dataSource={result}
            loading={isSearching}
            scroll={{ x: true }}
            bordered
            pagination={tableParams.pagination}
            // pagination={{ defaultPageSize: 10, total: tableData.pagination.total,
            // onChange: (page, pageSize) => {
            //     fetch({ pageSize, current: page });
            // },
            // showSizeChanger: true,position: ["bottomCenter"]}}>
            onChange={handleTableChange}
          ></R.StyledTable>
        </S.Card>
      </S.TablesWrapper>
    </>
  );
});

export default ReportSearchResultComponent;
